import { useGtm } from '@/composition/useGtm'
import utils from '@/utils'
import * as loginService from '@/service/login'
import * as memberService from '@/service/member'
import { GtmEventMap } from '@/view-models/gtm'
import { useAlertStore } from '@/store-pinia/alert'

export interface UserContract {
  isTermsAccepted: boolean
  isMarketingAllowed: boolean
}

export interface UseThirdLoginOptions {
  onSuccess?: () => void
}

export type RegisterSourceOptions = {
  byFacebook?: boolean
  byGoogle?: boolean
  byApple?: boolean
  byGoogleOneTap?: boolean
}

export type TrackingInfo = {
  loginType: string
  authMethod: string
}

export function useThirdLogin(options: UseThirdLoginOptions) {
  const nuxtApp = useNuxtApp()
  const { $axios, $store: store } = nuxtApp
  const { sendGtmEvent } = useGtm()

  const alertStore = useAlertStore()
  const registerSourceOptions = ref<RegisterSourceOptions>({})

  const regionId = computed(
    () => store.getters['region/region'].id || store.getters['branch/branch'].region_id || store.getters['product/productInfo'].region_id
  )

  const showConsent = ref(false)
  const pendingUserInfo = ref<Record<string, any> | null>(null)

  function onLoginSuccess(uid: string, tracking: TrackingInfo) {
    sendGtmEvent({
      event: GtmEventMap.ACCOUNT_LOGGED_IN,
      payload: {
        'Login Type': tracking.loginType,
      },
    })
    sendTrackingCode(uid, GtmEventMap.LOGIN, tracking)
    options.onSuccess?.()
  }

  function onNeedRegister(userInfo: Record<string, any>) {
    pendingUserInfo.value = userInfo
    showConsent.value = true
  }

  async function handleConsentConfirm(userContract: UserContract, tracking: TrackingInfo) {
    showConsent.value = false

    if (!pendingUserInfo.value) {
      return
    }

    await registerWithConsent(userContract, tracking)
  }

  async function registerWithConsent(userContract: UserContract, tracking: TrackingInfo) {
    try {
      const registerData = {
        ...pendingUserInfo.value,
        osVersion: navigator.userAgent,
        deviceId: utils.UUID,
        userContract,
      }

      const registerResult = await loginService.register($axios as any, registerData as any, registerSourceOptions.value)

      if (registerResult.code !== 0) {
        throw registerResult
      }

      await handlePostRegistration(registerResult, tracking)

      pendingUserInfo.value = null
      return true
    } catch (error: any) {
      openErrorDialog(error instanceof Error ? error.message : String(error))
      return false
    }
  }

  async function handlePostRegistration(registerResult: any, tracking: TrackingInfo) {
    let userInfo = registerResult.data
    if (regionId.value) {
      await memberService.updateMemberRegion($axios as any, regionId.value)

      userInfo = {
        ...userInfo,
        region_id: regionId.value,
      }
      if (store.getters['authentication/getUser']) {
        store.commit('authentication/UPDATE_USER_REGION_ID', regionId.value)
      }
    }
    nuxtApp.$auth.$storage.setUniversal('user', userInfo)
    store.commit('authentication/SET_USER', userInfo)
    sendTrackingCode(registerResult.data.uid, GtmEventMap.REGISTRATION, tracking)
    options.onSuccess?.()
  }

  function sendTrackingCode(UID: string, trackingType: string, tracking: TrackingInfo) {
    if (!UID) {
      return
    }

    nuxtApp.$gtm.push({
      event: trackingType,
      authenticationMethod: tracking.authMethod,
      userId: UID,
    })
  }

  function openErrorDialog(message: string) {
    alertStore.showAlert({ message: message, type: 'error' })
  }

  function resetConsent() {
    showConsent.value = false
    pendingUserInfo.value = null
  }

  return {
    showConsent,
    pendingUserInfo,
    onLoginSuccess,
    onNeedRegister,
    handleConsentConfirm,
    openErrorDialog,
    resetConsent,
    registerSourceOptions,
  }
}
